import { useState, useCallback } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    demo?: TourDemoType
}

const TOUR_STEPS: TourStep[] = [
    // Button: Classify Lead
    {
        selector: '[data-tour="btn-classify-lead"]',
        title: 'Phân Loại Lead',
        description:
            'Click nút này để phân loại khách hàng theo tiêu chí BANT:\n• Warm Lead: Tiềm năng cao\n• Cool Lead: Tiềm năng thấp\n• Unqualified: Không đủ điều kiện',
        demo: 'classify-lead-modal',
    },
    // Button: Unfollow Lead
    {
        selector: '[data-tour="btn-unfollow-lead"]',
        title: 'Bỏ Theo Dõi Lead',
        description:
            'Click để bỏ theo dõi lead:\n• Chuyển khách hàng sang trạng thái Dead Lead\n• Nhập lý do bỏ theo dõi\n• Có thể phục hồi sau',
    },
    // Button: Convert to Company
    {
        selector: '[data-tour="btn-convert-company"]',
        title: 'Chuyển Thành Công Ty',
        description:
            'Nâng cấp khách hàng cá nhân thành công ty:\n• Thêm thông tin công ty (tên, MST, địa chỉ)\n• Liên kết với khách hàng hiện tại\n• Chỉ khả dụng khi chưa có công ty',
    },
    // Button: Delete Customer
    {
        selector: '[data-tour="btn-delete-customer"]',
        title: 'Xoá Khách Hàng',
        description:
            'Xoá khách hàng khỏi hệ thống:\n• Chỉ xoá được khách hàng Dead Lead\n• Cần nhập lý do xoá\n• Hành động không thể hoàn tác',
    },
    // Button: Add Contact
    {
        selector: '[data-tour="btn-add-contact"]',
        title: 'Thêm Liên Hệ Mới',
        description:
            'Click nút này để thêm người liên hệ:\n• Tên, email, số điện thoại\n• Vai trò: Kỹ thuật, tài chính, hành chính...\n• Có thể tạo tài khoản đăng nhập Portal',
        demo: 'add-contact-modal',
    },
    // Button: Add Note
    {
        selector: '[data-tour="btn-add-note"]',
        title: 'Thêm Ghi Chú',
        description:
            'Click nút này để ghi chú nội bộ:\n• Ghi chú tình trạng, yêu cầu\n• Ghim ghi chú quan trọng\n• Lịch sử ghi chú được lưu trong Log',
        demo: 'add-note-modal',
    },
    // Company profile section
    {
        selector: '[data-tour="company-profile"]',
        title: 'Thông Tin Công Ty',
        description:
            'Thông tin công ty của khách hàng (nếu có):\n• Tên công ty, mã số thuế\n• Địa chỉ, số điện thoại\n• Người đại diện\n• Click để xem chi tiết',
    },
    // Contacts section
    {
        selector: '[data-tour="contacts-section"]',
        title: 'Danh Sách Liên Hệ',
        description:
            'Tất cả người liên hệ của khách hàng:\n• Thông tin: Tên, email, SĐT\n• Vai trò: Kỹ thuật, tài chính, hành chính...\n• Tài khoản: Trạng thái đăng nhập Portal',
    },
    // Pinned notes section
    {
        selector: '[data-tour="pinned-notes"]',
        title: 'Ghi Chú Quan Trọng',
        description:
            'Các ghi chú được ghim:\n• Hiển thị ghi chú quan trọng\n• Ghim/bỏ ghim để ưu tiên\n• Click để xem hoặc chỉnh sửa',
    },
    // Customer logs section
    {
        selector: '[data-tour="customer-logs"]',
        title: 'Lịch Sử Hoạt Động',
        description:
            'Timeline các hoạt động của khách hàng:\n• Tạo/cập nhật thông tin\n• Giao dịch, hợp đồng\n• Tương tác với nhân viên\n• Lọc theo loại hoạt động',
    },
]

export const useBasicInformationTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                    demo: step.demo,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible elements to tour')
            return
        }

        setIsActive(true)
        setCurrentStep(0)
        showTourStep(0, visibleSteps)
    }, [getVisibleSteps, showTourStep])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useBasicInformationTour
